import { useEffect, useContext, useState, useMemo, useRef } from 'react';
import DatePicker from '../../components/dateComponents/DatePicker2';
import { useGlobalStore, ModalContext } from '../../components/contexts';
import ValidateService from '../../services/ValidateService';
import SurveyListController from '../../controllers/aia/SurveyListController';
import { useNavigate } from 'react-router-dom';
import { formatDateToISOString } from '../../services/UtilService';

import GbitTable from '../../components/tableComponents/GbitTable';
import SelectEnt from '../../components/selectComponents/SelectEnt';

const DateFilter = ({
  onDateChange,
  customStartDate,
  customEndDate,
  setCustomStartDate,
  setCustomEndDate,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState('전체');
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);

  const handlePeriodSelect = label => {
    setSelectedPeriod(label);

    const today = new Date();
    let startDate = null;
    let endDate = new Date();

    if (label === '3개월') {
      startDate = new Date(today.setMonth(today.getMonth() - 3));
    } else if (label === '6개월') {
      startDate = new Date(today.setMonth(today.getMonth() - 6));
    } else if (label === '1년') {
      startDate = new Date(today.setFullYear(today.getFullYear() - 1));
    } else if (label === '2년') {
      startDate = new Date(today.setFullYear(today.getFullYear() - 2));
    } else if (label === '3년') {
      startDate = new Date(today.setFullYear(today.getFullYear() - 3));
    } else if (label === '전체') {
      startDate = null;
      endDate = null;
    }

    const formattedStartDate = startDate
      ? formatDateToISOString(startDate)
      : null;
    const formattedEndDate = endDate
      ? formatDateToISOString(endDate, true)
      : null;

    onDateChange({
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    });

    // 기간 조회용 날짜 상태 초기화
    if (label !== '기간 조회') {
      setCustomStartDate(null);
      setCustomEndDate(null);
    }
  };

  return (
    <td>
      <div className="input-group">
        {['전체', '3개월', '6개월', '1년', '2년', '3년', '기간 조회'].map(
          (label, idx) => (
            <div className="form-chip sm" key={idx}>
              <input
                type="radio"
                className="radio"
                name="rdo_chip_period"
                id={`rdo_chip_period_${idx}`}
                checked={selectedPeriod === label}
                onChange={() => handlePeriodSelect(label)}
              />
              <label
                className="form-chip-outline"
                htmlFor={`rdo_chip_period_${idx}`}
              >
                {label}
              </label>
            </div>
          )
        )}
      </div>
      {selectedPeriod === '기간 조회' && (
        <div className="input-group gap-md">
          <DatePicker
            isOpen={startDatePickerOpen}
            setIsOpen={setStartDatePickerOpen}
            label="시작 날짜"
            selectedDate={customStartDate}
            onDateChange={date => {
              setCustomStartDate(date);
              onDateChange({
                startDate: formatDateToISOString(date),
                endDate: customEndDate
                  ? formatDateToISOString(customEndDate, true)
                  : null,
              });
            }}
          />
          <DatePicker
            isOpen={endDatePickerOpen}
            setIsOpen={setEndDatePickerOpen}
            label="종료 날짜"
            selectedDate={customEndDate}
            onDateChange={date => {
              setCustomEndDate(date);
              onDateChange({
                startDate: customStartDate
                  ? formatDateToISOString(customStartDate)
                  : null,
                endDate: formatDateToISOString(date, true),
              });
            }}
          />
        </div>
      )}
    </td>
  );
};

const SurveyList = () => {
  useEffect(() => {
    document.title = '유해요인조사관리 목록';
  }, []);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const { store, updateStore } = useGlobalStore();
  const { modalService } = useContext(ModalContext);
  const validateService = new ValidateService(modalService);
  const [filter, setFilter] = useState({
    startDate: null,
    endDate: null,
    selectedStatus: '',
  });

  const handleSearch = () => {
    const updatedFilter = {
      startDate: customStartDate
        ? formatDateToISOString(customStartDate)
        : filter.startDate,
      endDate: customEndDate
        ? formatDateToISOString(customEndDate, true)
        : filter.endDate,
      selectedStatus: selectedStatus || '',
    };

    setSearchParam(prevState => {
      const updatedSearchParam = {
        ...prevState,
        ...updatedFilter,
        currentPage: 1,
      };

      controller.fnSearch(1, updatedSearchParam);
      return updatedSearchParam;
    });
  };

  const tableRef = useRef(null);
  const [searchParam, setSearchParam] = useState({
    entSn: store.entSn,
    pageSize: 10,
  });
  const [dataList, setDataList] = useState([]);
  const navigate = useNavigate(); // navigate 함수를 가져옴

  const controller = useMemo(() => {
    return new SurveyListController(
      store,
      modalService,
      validateService,
      [tableRef],
      [searchParam, setSearchParam],
      {
        dataList,
        setDataList,
      },
      navigate // navigate 함수를 전달
    );
  }, [store, modalService, validateService]);

  useEffect(() => {
    controller.init();
  }, []);

  useEffect(() => {}, [searchParam]);

  return (
    <>
      <div className="page-title-wrap" data-type="responsive">
        <h2 className="h-tit">유해요인조사관리</h2>
      </div>
      {store.authrtId === 'ADMIN' && (
        <div className="tbl-wrap">
          <p className="tbl-msg">
            <em className="star-require">*</em> 표시는 필수 입력 사항입니다.
          </p>
          <table className="tbl data row">
            <colgroup>
              <col />
              <col />
            </colgroup>
            <tbody>
              <tr>
                <th scope="row">
                  기업<em className="star-require">*</em>
                </th>
                <td>
                  <div className="input-group">
                    <SelectEnt
                      model="entSn"
                      valueObj={searchParam}
                      valueChange={(model, value) => {
                        controller.searchParamChange(model, value);
                      }}
                    />
                    <button
                      type="button"
                      className="btn md primary"
                      onClick={() => controller.fnSearch()}
                    >
                      조회
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <div className="tbl-wrap">
        <table className="tbl data row">
          <tbody>
            <tr>
              <th scope="row">조회 기간</th>
              <DateFilter
                onDateChange={updatedFilter =>
                  setFilter(prev => ({ ...prev, ...updatedFilter }))
                }
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                setCustomStartDate={setCustomStartDate}
                setCustomEndDate={setCustomEndDate}
              />
            </tr>
            <tr>
              <th scope="row">조회 구분</th>
              <td>
                <div className="input-group">
                  <label className="hidden-label" htmlFor="sel1" id="sel1Lbl">
                    라벨
                  </label>
                  <select
                    id="sel1"
                    className="form-select md"
                    value={selectedStatus}
                    onChange={e => setSelectedStatus(e.target.value)}
                    aria-labelledby="sel1Lbl"
                  >
                    <option value="">현재 상태</option>
                    <option value="체크리스트">체크리스트</option>
                    <option value="영상업로드">영상업로드</option>
                    <option value="기본조사표">기본조사표</option>
                    <option value="증상조사표">증상조사표</option>
                    <option value="조사완료">조사완료</option>
                  </select>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="page-btn-wrap gap-lg">
        <button
          type="button"
          className="btn xlg primary"
          onClick={handleSearch}
        >
          검색하기
        </button>
      </div>
      <div className="tbl-wrap">
        <GbitTable
          ref={tableRef}
          tableName="surveyListPage"
          headerList={controller.headerList}
          bodyList={dataList}
          onHeaderClick={controller.tableHeaderClick.bind(controller)}
          onCellClick={controller.tableCellClick.bind(controller)}
          pageNow={searchParam?.currentPage}
          pageSize={searchParam?.pageSize}
          pageChange={value =>
            controller.searchParamChange('currentPage', value)
          }
          dataOption={controller.dataOption}
        />
      </div>
      <div className="page-btn-wrap">
        {/* <select
        id="select"
        className="form-select md"
        value={searchParam.seNm || '정기/수시 선택'}
        onChange={e => controller.handleChange(e.target.value)}
        style={{width:"17rem", height: "5.6rem"}}
      >
        <option value="정기/수시 선택"disabled>정기/수시 선택</option>
        <option value="정기">정기</option>
        <option value="수시">수시</option>
      </select> */}
        {/* <button
          type="button"
          className="btn xlg primary"
          onClick={() => controller.fnNewTask(searchParam.seNm)}
        >
          신규조사등록
        </button> */}
      </div>
    </>
  );
};

export default SurveyList;
